import React, { useRef, useState, useEffect, useMemo } from 'react';
import ProMarketingLink from './ProMarketingLink';
import { useTranslation } from 'react-i18next';
import { motion, useScroll, useTransform, useSpring } from 'framer-motion';
import { 
  MoreVertical, 
  ArrowRight,
  Calendar,
  Clock,
  MapPin,
  User,
  MessageCircle,
  Check,
  X,
  Pencil,
  Send
} from 'lucide-react';

/* 
   ==========================================================================
   ULTRA-HIGH FIDELITY MOCK COMPONENTS (IDENTIQUES À PRÉCÉDEMMENT)
   ==========================================================================
*/

const SERVICE_CARD_META = [
  { duration: 30, price: 20, clientsMax: 1, borderColor: '#22c55e', img: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?auto=format&fit=crop&w=400&q=80' },
  { duration: 120, price: 100, clientsMax: 1, borderColor: '#8b5cf6', img: 'https://images.unsplash.com/photo-1551730459-92db2a308d6a?auto=format&fit=crop&w=400&q=80' },
  { duration: 60, price: 45, clientsMax: 1, borderColor: '#A5765C', img: 'https://images.unsplash.com/photo-1583511655857-d19b40a7a54e?auto=format&fit=crop&w=400&q=80' },
  { duration: 90, price: 25, clientsMax: 6, borderColor: '#0ea5e9', img: 'https://images.unsplash.com/photo-1548199973-03cce0bbc87b?auto=format&fit=crop&w=400&q=80' },
] as const;

type MockServiceItem = {
  name: string;
  desc: string;
  payment: string;
  location: string;
};

const MockServicesContent = () => {
  const { t } = useTranslation('marketing');
  const ns = 'marketingWorkflow.mockServices';
  const items = t(`${ns}.items`, { returnObjects: true }) as MockServiceItem[];

  return (
  <div className="mw-mock mw-mock--services" style={{ padding: '32px', height: '100%', overflowY: 'auto', background: '#fff', display: 'flex', flexDirection: 'column' }}>
    <div className="mw-mock__head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexShrink: 0 }}>
      <div>
        <div style={{ fontSize: '22px', fontWeight: 800, color: '#1e293b', margin: 0, fontFamily: 'Plus Jakarta Sans, sans-serif', letterSpacing: '-0.02em' }}>{t(`${ns}.title`)}</div>
        <p style={{ fontSize: '14px', color: '#64748b', margin: '6px 0 0 0', fontWeight: 500 }}>{t(`${ns}.subtitle`)}</p>
      </div>
      <button className="mw-mock__new" style={{ background: '#A5765C', color: '#fff', border: 'none', padding: '12px 20px', borderRadius: '14px', fontWeight: 800, fontSize: '14px', boxShadow: '0 4px 14px rgba(165,118,92,0.25)', cursor: 'pointer', whiteSpace: 'nowrap' }}>
        {t(`${ns}.newService`)}
      </button>
    </div>

    <div className="mw-mock__grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', flex: 1, minHeight: '220px', alignItems: 'stretch' }}>
      {SERVICE_CARD_META.map((meta, idx) => {
        const s = items[idx];
        if (!s) return null;
        const isPeek = idx >= 2;
        const card = (
          <div
            key={idx}
            style={{
              background: '#fff',
              borderRadius: '16px',
              border: `2px solid ${meta.borderColor}`,
              boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              height: isPeek ? undefined : '100%'
            }}
          >
            <div style={{ flex: '0 0 80px', overflow: 'hidden', borderTopLeftRadius: '14px', borderTopRightRadius: '14px' }}>
              <img src={meta.img} alt={s.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
            <div style={{ padding: '12px', flex: isPeek ? undefined : 1, display: 'flex', flexDirection: 'column', minHeight: isPeek ? undefined : 0 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '6px', paddingRight: '4px' }}>
                <div style={{ fontWeight: 800, fontSize: '13px', color: '#1e293b', margin: 0, fontFamily: 'Plus Jakarta Sans, sans-serif', lineHeight: 1.25, flex: 1 }}>{s.name}</div>
                {!isPeek && <MoreVertical size={16} color="#94a3b8" style={{ flexShrink: 0 }} />}
              </div>
              {!isPeek && (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', fontSize: '11px', color: '#22c55e', fontWeight: 700 }}>
                    <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e' }} />
                    {t(`${ns}.visible`)}
                  </div>
                  <p style={{ fontSize: '11px', color: '#64748b', margin: '0 0 10px 0', lineHeight: 1.4, display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' as any, overflow: 'hidden' }}>{s.desc} <span style={{ color: '#A5765C', fontWeight: 700 }}>{t(`${ns}.seeMore`)}</span></p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                    <span style={{ padding: '4px 8px', background: '#f1f5f9', borderRadius: '8px', fontSize: '11px', fontWeight: 700, color: '#475569' }}>{meta.duration} min</span>
                    <span style={{ padding: '4px 8px', background: '#f1f5f9', borderRadius: '8px', fontSize: '11px', fontWeight: 700, color: '#475569' }}>{meta.price}€</span>
                    <span style={{ padding: '4px 8px', background: '#f1f5f9', borderRadius: '8px', fontSize: '11px', fontWeight: 700, color: '#475569' }}>{t(`${ns}.client`, { count: meta.clientsMax })}</span>
                    <span style={{ padding: '4px 8px', background: '#f1f5f9', borderRadius: '8px', fontSize: '11px', fontWeight: 700, color: '#475569' }}>{s.payment}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#64748b', fontWeight: 600 }}>
                    <MapPin size={12} />
                    {s.location}
                  </div>
                </>
              )}
            </div>
          </div>
        );
        if (isPeek) {
          return (
            <div
              key={idx}
              className="mw-mock__peek"
              style={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '16px',
                height: '100%',
                maxHeight: '100px'
              }}
            >
              {card}
              <div
                style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  right: 0,
                  height: '32px',
                  background: 'linear-gradient(to top, rgba(255,255,255,0.95), transparent)',
                  pointerEvents: 'none'
                }}
              />
            </div>
          );
        }
        return card;
      })}
    </div>
  </div>
  );
};

const MockReservationsContent = () => {
  const { t } = useTranslation('marketing');
  const ns = 'marketingWorkflow.mockReservations';
  const items = t(`${ns}.items`, { returnObjects: true }) as Array<{
    service: string;
    date: string;
    time: string;
    location: string;
    client: string;
    animal: string;
  }>;

  return (
  <div className="mw-mock mw-mock--reservations" style={{ padding: '24px', height: '100%', overflowY: 'auto', background: '#f8fafc' }}>
    <div className="mw-mock__tabs" style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
      <span style={{ padding: '8px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: 800, color: '#64748b', background: '#fff', border: '1px solid #e2e8f0' }}>{t(`${ns}.tabRequests`)}</span>
      <span style={{ padding: '8px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: 800, color: '#1e293b', background: '#fff', border: '1px solid #A5765C', position: 'relative' }}>
        {t(`${ns}.tabPending`)}
        <span style={{ position: 'absolute', top: '-4px', right: '-4px', background: '#3b82f6', color: '#fff', fontSize: '10px', fontWeight: 900, width: '18px', height: '18px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>4</span>
      </span>
      <span style={{ padding: '8px 16px', borderRadius: '10px', fontSize: '13px', fontWeight: 800, color: '#64748b', background: '#fff', border: '1px solid #e2e8f0' }}>{t(`${ns}.tabOther`)}</span>
    </div>
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {items.map((r, idx) => (
        <div key={idx} className="mw-mock__card" style={{
          background: '#fff',
          borderRadius: '20px',
          border: '1px solid rgba(165, 118, 92, 0.12)',
          boxShadow: '0 4px 20px rgba(0,0,0,0.04)',
          overflow: 'hidden'
        }}>
          <div style={{ padding: '20px', borderBottom: '1px solid #f1f5f9' }}>
            <div style={{ fontSize: '16px', fontWeight: 800, color: '#1e293b', margin: '0 0 14px 0', fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{r.service}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px', color: '#475569' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Calendar size={14} color="#64748b" />
                <span>{r.date}</span>
                <Clock size={14} color="#64748b" style={{ marginLeft: '12px' }} />
                <span>{r.time}</span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <MapPin size={14} color="#64748b" />
                <span>{r.location}</span>
              </div>
            </div>
          </div>
          <div style={{ padding: '16px 20px', background: '#fff' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px', fontSize: '13px' }}>
              <User size={14} color="#64748b" />
              <span style={{ fontWeight: 700, color: '#1e293b' }}>{r.client}</span>
            </div>
            <div style={{ fontSize: '12px', color: '#64748b', marginBottom: '12px' }}>{r.animal}</div>
            <div style={{ fontSize: '12px', color: '#94a3b8' }}>
              <MessageCircle size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
              {t(`${ns}.clientMessage`)} <span style={{ color: '#A5765C', fontWeight: 600 }}>{t(`${ns}.noMessage`)}</span>
            </div>
          </div>
          <div className="mw-mock__actions" style={{ padding: '12px 20px', display: 'flex', gap: '10px', flexWrap: 'wrap', borderTop: '1px solid #f1f5f9' }}>
            <button style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#22c55e', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}><Check size={14} /> {t(`${ns}.accept`)}</button>
            <button style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#ef4444', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}><X size={14} /> {t(`${ns}.decline`)}</button>
            <button style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#fff', color: '#d97706', border: '1px solid #d97706', borderRadius: '10px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}><Pencil size={14} /> {t(`${ns}.edit`)}</button>
            <button style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', background: '#d97706', color: '#fff', border: 'none', borderRadius: '10px', fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}><Send size={14} /> {t(`${ns}.followUp`)}</button>
          </div>
        </div>
      ))}
    </div>
  </div>
  );
};

const PLANNING_EVENT_META: Array<{ col: number; top: string; height: string; color: string; isGoogle?: boolean }> = [
  { col: 0, top: '0%', height: '22%', color: 'linear-gradient(180deg, #dcfce7 0%, #bbf7d0 100%)', isGoogle: false },
  { col: 0, top: '24%', height: '18%', color: '#A5765C', isGoogle: false },
  { col: 0, top: '78%', height: '20%', color: '#94a3b8', isGoogle: true },
  { col: 1, top: '0%', height: '28%', color: '#8b5cf6', isGoogle: false },
  { col: 1, top: '30%', height: '18%', color: 'linear-gradient(180deg, #dcfce7 0%, #bbf7d0 100%)', isGoogle: false },
  { col: 1, top: '52%', height: '16%', color: '#e2e8f0', isGoogle: true },
  { col: 2, top: '0%', height: '20%', color: '#f59e0b', isGoogle: false },
  { col: 2, top: '22%', height: '22%', color: '#cbd5e1', isGoogle: true },
  { col: 2, top: '48%', height: '24%', color: '#A5765C', isGoogle: false },
  { col: 3, top: '0%', height: '26%', color: '#8b5cf6', isGoogle: false },
  { col: 3, top: '28%', height: '20%', color: '#e2e8f0', isGoogle: true },
  { col: 3, top: '52%', height: '18%', color: '#22c55e', isGoogle: false },
  { col: 4, top: '0%', height: '22%', color: 'linear-gradient(180deg, #dcfce7 0%, #bbf7d0 100%)', isGoogle: false },
  { col: 4, top: '24%', height: '20%', color: '#A5765C', isGoogle: false },
  { col: 4, top: '46%', height: '20%', color: '#A5765C', isGoogle: false },
  { col: 4, top: '70%', height: '16%', color: '#cbd5e1', isGoogle: true },
  { col: 5, top: '0%', height: '24%', color: '#22c55e', isGoogle: false },
  { col: 5, top: '26%', height: '22%', color: '#8b5cf6', isGoogle: false },
  { col: 5, top: '52%', height: '18%', color: '#e2e8f0', isGoogle: true },
  { col: 6, top: '0%', height: '20%', color: '#A5765C', isGoogle: false },
  { col: 6, top: '24%', height: '22%', color: '#f1f5f9', isGoogle: true },
];

const MockPlanningContent = () => {
  const { t } = useTranslation('marketing');
  const ns = 'marketingWorkflow.mockPlanning';
  const viewLabels = ['today', 'day', 'week', 'month'] as const;
  const dayHeaders = t(`${ns}.dayHeaders`, { returnObjects: true }) as string[];
  const events = t(`${ns}.events`, { returnObjects: true }) as Array<{ label: string; time: string }>;
  const planningEvents = useMemo(
    () =>
      PLANNING_EVENT_META.map((meta, i) => ({
        ...meta,
        label: events[i]?.label ?? '',
        time: events[i]?.time ?? '',
      })),
    [events]
  );

  return (
  <div className="mw-mock mw-mock--planning" style={{ height: '100%', background: '#fff', padding: '16px', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
    <div className="mw-mock__toolbar" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px', flexShrink: 0 }}>
      <div style={{ display: 'flex', gap: '6px' }}>
        {viewLabels.map((key, i) => (
          <span
            key={key}
            style={{
              padding: '6px 12px',
              borderRadius: '10px',
              fontSize: '11px',
              fontWeight: 800,
              color: i === 2 ? '#fff' : '#64748b',
              background: i === 2 ? '#A5765C' : '#f1f5f9',
              cursor: 'pointer'
            }}
          >
            {t(`${ns}.${key}`)}
          </span>
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', fontWeight: 800, color: '#1e293b' }}>
        <span>{t(`${ns}.weekOf`)}</span>
        <button type="button" tabIndex={-1} aria-hidden="true" className="lp-mock-chrome-btn" style={{ background: 'none', border: 'none', padding: '2px', color: '#64748b', fontSize: '14px', pointerEvents: 'none' }}>‹</button>
        <button type="button" tabIndex={-1} aria-hidden="true" className="lp-mock-chrome-btn" style={{ background: 'none', border: 'none', padding: '2px', color: '#64748b', fontSize: '14px', pointerEvents: 'none' }}>›</button>
      </div>
    </div>
    <div style={{ display: 'flex', gap: '8px', marginBottom: '8px', flexShrink: 0, fontSize: '10px', color: '#94a3b8', fontWeight: 700 }}>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '8px', height: '8px', borderRadius: '4px', background: '#A5765C' }} /> {t(`${ns}.legendPro`)}</span>
      <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}><span style={{ width: '8px', height: '8px', borderRadius: '4px', background: '#e2e8f0' }} /> {t(`${ns}.legendPersonal`)}</span>
    </div>
    <div className="mw-mock__week-scroll" style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
    <div className="mw-mock__week" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '6px', flex: 1, minHeight: '240px' }}>
      {dayHeaders.map((day, colIndex) => {
        const dayEvents = planningEvents.filter((e) => e.col === colIndex);
        return (
          <div key={day} style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
            <div style={{ textAlign: 'center', fontSize: '10px', fontWeight: 800, color: colIndex === 0 ? '#A5765C' : '#94a3b8' }}>{day}</div>
            <div style={{ flex: 1, background: '#f8fafc', borderRadius: '10px', position: 'relative', padding: '4px', minHeight: '180px' }}>
              {dayEvents.length === 0 && (
                <div style={{ padding: '8px', fontSize: '9px', color: '#94a3b8', textAlign: 'center' }}>{t(`${ns}.noEvents`)}</div>
              )}
              {dayEvents.map((ev, i) => {
                const isGradient = ev.color.startsWith('linear');
                const isLightBg = ['#e2e8f0', '#cbd5e1', '#f1f5f9', '#94a3b8'].includes(ev.color);
                const textColor = isGradient ? '#166534' : (ev.isGoogle || isLightBg ? '#475569' : '#fff');
                return (
                  <div
                    key={i}
                    style={{
                      position: 'absolute',
                      left: '4px',
                      right: '4px',
                      top: ev.top,
                      height: ev.height,
                      background: isGradient ? undefined : ev.color,
                      backgroundImage: isGradient ? ev.color : undefined,
                      border: isGradient ? '1px solid #86efac' : ev.isGoogle ? '1px solid #cbd5e1' : undefined,
                      borderRadius: '8px',
                      padding: '6px',
                      overflow: 'hidden',
                      fontSize: '9px',
                      fontWeight: 700,
                      color: textColor,
                      lineHeight: 1.2,
                      boxSizing: 'border-box'
                    }}
                  >
                    <span style={{ opacity: 0.9 }}>{ev.time}</span>
                    <br />
                    <span style={{ fontWeight: 800 }}>{ev.label}</span>
                    {ev.isGoogle && <span style={{ marginLeft: '4px', fontSize: '8px', opacity: 0.8 }}>📅</span>}
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
    </div>
  </div>
  );
};

// Animation Wrapper for visuals (STAY STICKY)
const WorkflowVisualItem = ({ step, index, progress, total }: { step: any, index: number, progress: any, total: number }) => {
  const isFirst = index === 0;
  const isLast = index === total - 1;
  const start = index / total;
  const end = (index + 1) / total;
  
  // Transition logic
  const threshold = 0.15; 
  const range = [start, start + threshold, end - threshold, end];
  
  const opacityValues = isFirst ? [1, 1, 1, 0] : (isLast ? [0, 1, 1, 1] : [0, 1, 1, 0]);
  const scaleValues = isFirst ? [1, 1, 1, 0.9] : (isLast ? [0.9, 1, 1, 1] : [0.9, 1, 1, 0.9]);
  const yValues = isFirst ? [0, 0, 0, -80] : (isLast ? [80, 0, 0, 0] : [80, 0, 0, -80]);
  const filterValues = isFirst ? ["blur(0px)", "blur(0px)", "blur(0px)", "blur(15px)"] : (isLast ? ["blur(15px)", "blur(0px)", "blur(0px)", "blur(0px)"] : ["blur(15px)", "blur(0px)", "blur(0px)", "blur(15px)"]);

  const opacity = useTransform(progress, range, opacityValues);
  const scale = useTransform(progress, range, scaleValues);
  const y = useTransform(progress, range, yValues);
  const filter = useTransform(progress, range, filterValues);

  const VisualContent = step.visual;

  return (
    <motion.div
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity,
        scale,
        y,
        filter,
        zIndex: index + 1,
        willChange: 'transform, opacity, filter'
      }}
    >
      <div style={{
        width: '100%',
        height: '100%',
        background: 'white',
        borderRadius: '40px',
        boxShadow: '0 50px 100px rgba(0,0,0,0.1), 0 0 0 1px rgba(0,0,0,0.05)',
        overflow: 'hidden',
        border: '1px solid rgba(0,0,0,0.05)'
      }}>
        <VisualContent />
      </div>
    </motion.div>
  );
};

// Text Section (DÉMONSTRATION 01, 02, 03) — aligné au design landing
const WorkflowTextSection = ({ step }: { step: any }) => {
  const { t } = useTranslation('marketing');
  const ns = 'marketingWorkflow';

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      gap: '20px',
      padding: '0 20px'
    }}>
      <div style={{
        fontFamily: 'Inter',
        fontSize: '12px',
        fontWeight: 800,
        color: '#94a3b8',
        letterSpacing: '0.2em',
        textTransform: 'uppercase',
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
      }}>
        <div style={{ width: '24px', height: '1px', background: '#cbd5e1' }} />
        {t(`${ns}.demonstration`, { order: step.order })}
      </div>

      <div>
        <h2 style={{
          fontSize: 'clamp(36px, 4.5vw, 52px)',
          fontWeight: 900,
          margin: 0,
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          color: '#1e293b',
          lineHeight: 0.98,
          letterSpacing: '-0.04em'
        }}>
          {step.title}
        </h2>
        <p style={{
          fontSize: '18px',
          fontWeight: 600,
          color: '#64748b',
          margin: '10px 0 0 0',
          fontFamily: 'Inter'
        }}>
          {step.duration}
        </p>
      </div>

      <p style={{
        fontFamily: 'Inter',
        fontSize: '17px',
        lineHeight: 1.6,
        color: '#64748b',
        margin: 0,
        maxWidth: '420px',
        fontWeight: 500
      }}>
        {step.description}
      </p>

      {step.detailUrl ? (
        <ProMarketingLink
          to={step.detailUrl}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '10px',
            marginTop: '4px',
            padding: '14px 22px',
            background: '#334155',
            borderRadius: '14px',
            width: 'fit-content',
            cursor: 'pointer',
            boxShadow: '0 4px 14px rgba(30,41,59,0.25)',
            border: 'none',
            textDecoration: 'none'
          }}
        >
          <span style={{ fontSize: '14px', fontWeight: 800, color: '#fff', fontFamily: 'Inter' }}>{t(`${ns}.viewFeatureDetail`)}</span>
          <ArrowRight size={18} color="#fff" strokeWidth={2.5} />
        </ProMarketingLink>
      ) : (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '10px',
          marginTop: '4px',
          padding: '14px 22px',
          background: '#334155',
          borderRadius: '14px',
          width: 'fit-content',
          cursor: 'pointer',
          boxShadow: '0 4px 14px rgba(30,41,59,0.25)',
          border: 'none'
        }}>
          <span style={{ fontSize: '14px', fontWeight: 800, color: '#fff', fontFamily: 'Inter' }}>{t(`${ns}.viewFeatureDetail`)}</span>
          <ArrowRight size={18} color="#fff" strokeWidth={2.5} />
        </div>
      )}
    </div>
  );
};

const STEP_CONFIG = [
  { order: '01', key: 'services' as const, visual: MockServicesContent, detailUrl: '/fonctionnalites' },
  { order: '02', key: 'reservations' as const, visual: MockReservationsContent, detailUrl: '/fonctionnalites/planning-reservations' },
  { order: '03', key: 'planning' as const, visual: MockPlanningContent, detailUrl: '/fonctionnalites/double-sync-google' },
];

// Hook simple pour savoir si on est sur un viewport mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(max-width: 992px)').matches
      : false
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(max-width: 992px)');
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  return isMobile;
};

export default function MarketingWorkflow() {
  const { t } = useTranslation('marketing');
  const ns = 'marketingWorkflow';
  const containerRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  const steps = useMemo(
    () =>
      STEP_CONFIG.map((cfg) => ({
        order: cfg.order,
        title: t(`${ns}.steps.${cfg.key}.title`),
        duration: t(`${ns}.steps.${cfg.key}.duration`),
        description: t(`${ns}.steps.${cfg.key}.description`),
        visual: cfg.visual,
        detailUrl: cfg.detailUrl,
      })),
    [t]
  );

  // Scroll tracking pour le visuel sticky (desktop uniquement)
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"]
  });

  const smoothProgress = useSpring(scrollYProgress, {
    stiffness: 80,
    damping: 30,
    restDelta: 0.001
  });

  // ----- VERSION MOBILE : 3 "slides" plein écran, phone centré + texte court -----
  if (isMobile) {
    return (
      <div
        id="how-it-works"
        className="mk-workflow mk-workflow--mobile"
        style={{ position: 'relative', background: '#fafaf9' }}
      >
        {steps.map((step, index) => {
          const Visual = step.visual;
          return (
            <section
              key={step.order}
              className="mk-workflow__mobile-step"
              style={{
                minHeight: '100vh',
                padding: '96px 20px 64px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'space-between',
                maxWidth: '960px',
                margin: '0 auto',
              }}
            >
              {/* Texte condensé */}
              <div style={{ marginBottom: 32 }}>
                <div style={{
                  fontFamily: 'Inter',
                  fontSize: 11,
                  fontWeight: 800,
                  color: '#94a3b8',
                  letterSpacing: '0.22em',
                  textTransform: 'uppercase',
                  marginBottom: 10,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                }}>
                  <div style={{ width: 24, height: 1, background: '#cbd5e1' }} />
                  {t(`${ns}.demonstration`, { order: step.order })}
                </div>
                <h2 style={{
                  fontFamily: 'Plus Jakarta Sans, sans-serif',
                  fontSize: 28,
                  lineHeight: 1.05,
                  letterSpacing: '-0.05em',
                  margin: 0,
                  color: '#1e293b',
                }}>
                  {step.title}
                </h2>
                <p style={{
                  margin: '8px 0 0 0',
                  fontSize: 15,
                  fontWeight: 600,
                  color: '#64748b',
                }}>
                  {step.duration}
                </p>
                <p style={{
                  margin: '12px 0 0 0',
                  fontSize: 14,
                  lineHeight: 1.6,
                  color: '#64748b',
                }}>
                  {step.description}
                </p>
                {step.detailUrl && (
                  <ProMarketingLink
                    to={step.detailUrl}
                    className="lp-workflow-cta"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: 16,
                      padding: '12px 18px',
                      background: '#334155',
                      borderRadius: 12,
                      width: 'fit-content',
                      textDecoration: 'none',
                      boxShadow: '0 4px 14px rgba(30,41,59,0.25)',
                    }}
                  >
                    <span style={{ fontSize: 13, fontWeight: 800, color: '#fff', fontFamily: 'Inter' }}>{t(`${ns}.viewFeatureDetail`)}</span>
                    <ArrowRight size={16} color="#fff" strokeWidth={2.5} />
                  </ProMarketingLink>
                )}
              </div>

              {/* Phone mockup centré, très lisible */}
              <div className="mk-workflow__phone-wrap">
                <div
                  className="mk-workflow__phone"
                  style={{
                    width: '100%',
                    maxWidth: 380,
                    aspectRatio: '4 / 5',
                    background: 'white',
                    borderRadius: 32,
                    boxShadow: '0 30px 80px rgba(15,23,42,0.35)',
                    border: '1px solid rgba(148,163,184,0.25)',
                    overflow: 'hidden',
                  }}
                >
                  <Visual />
                </div>
              </div>
            </section>
          );
        })}
      </div>
    );
  }

  // ----- VERSION DESKTOP : inchangée (visuel sticky à gauche, texte à droite) -----
  return (
    <div
      id="how-it-works"
      ref={containerRef}
      className="mk-workflow"
      style={{ position: 'relative', background: '#fafaf9' }}
    >
      <div
        className="mk-workflow__container"
        style={{
          maxWidth: '1280px',
          margin: '0 auto',
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 0.7fr)',
          gap: '120px',
          padding: '0 60px',
        }}
      >
        
        {/* Visual Side (Pinned - STICKY) */}
        <div
          className="mk-workflow__visual-wrapper"
          style={{
            position: 'sticky',
            top: 0,
            height: '100vh',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <div className="mk-workflow__visual" style={{ position: 'relative', height: '600px', width: '100%' }}>
            {/* Background Glow */}
            <div style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: '120%',
              height: '110%',
              background: 'radial-gradient(circle, rgba(165, 118, 92, 0.08) 0%, transparent 80%)',
              filter: 'blur(80px)',
              zIndex: 0
            }} />
            
            {steps.map((step, index) => (
              <WorkflowVisualItem 
                key={index} 
                step={step} 
                index={index} 
                progress={smoothProgress} 
                total={steps.length} 
              />
            ))}
          </div>
        </div>

        {/* Text Side (Moving - NORMAL FLOW) */}
        <div className="mk-workflow__content-wrapper">
          {steps.map((step, index) => (
            <WorkflowTextSection key={index} step={step} />
          ))}
        </div>

      </div>
    </div>
  );
}
